import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { profileAPI } from "../services/api";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import defaultAvatar from "../assets/images/avatar.jpg";
import "./Profile.scss";

function Profile() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeEditSection, setActiveEditSection] = useState(null);
  
  const [profileData, setProfileData] = useState({
    // Profile Header
    profilePhoto: "",
    fullName: "",
    headline: "",
    profileCompleteness: 0,
    
    // Contact Information
    email: "",
    phone: "",
    addressStreet: "",
    addressCity: "",
    addressState: "",
    country: "",
    addressZip: "",
    
    // Professional Summary
    professionalSummary: "",
    
    // Skills (Top 5)
    skills: [],
    
    // Resume
    resumeUrl: "",
    resumeLastUpdated: null,
  });

  const [newSkill, setNewSkill] = useState("");
  const [tempSkills, setTempSkills] = useState([]); // Temporary skills for editing
  const [skillLimitNotification, setSkillLimitNotification] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCompletionBreakdown, setShowCompletionBreakdown] = useState(false);

  // Countries list
  const countries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", "Austria",
    "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin",
    "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso",
    "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile",
    "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
    "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
    "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia",
    "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
    "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
    "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos",
    "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar",
    "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico",
    "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
    "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia",
    "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru",
    "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
    "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
    "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
    "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden",
    "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga",
    "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates",
    "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
    "Yemen", "Zambia", "Zimbabwe"
  ].sort();

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const response = await profileAPI.getProfile();
        if (response.data.success) {
          const profile = response.data.data.profile;
          setProfileData({
            profilePhoto: profile.profilePhoto || "",
            fullName: profile.fullName || user?.fullName || "",
            headline: profile.headline || "",
            profileCompleteness: profile.profileCompleteness || 0,
            email: profile.email || user?.email || "",
            phone: profile.phone || "",
            addressStreet: profile.addressStreet || "",
            addressCity: profile.addressCity || "",
            addressState: profile.addressState || "",
            country: profile.country || "",
            addressZip: profile.addressZip || "",
            professionalSummary: profile.professionalSummary || "",
            skills: profile.skills || [],
            resumeUrl: profile.resumeUrl || "",
            resumeLastUpdated: profile.resumeLastUpdated || null,
          });
        }
      } catch (error) {
        console.error("Error loading profile:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadProfile();
    }
  }, [user]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.country-select-wrapper')) {
        setShowCountryDropdown(false);
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCountryDropdown]);

  // Reset country search when closing edit section
  useEffect(() => {
    if (activeEditSection !== "contact") {
      setCountrySearch("");
      setShowCountryDropdown(false);
    }
  }, [activeEditSection]);

  // Save section
  const saveSection = async (sectionData) => {
    try {
      setSaving(true);
      const response = await profileAPI.updateProfile(sectionData);
      if (response.data.success) {
        // Reload profile to get updated completeness from database
        const profileResponse = await profileAPI.getProfile();
        if (profileResponse.data.success) {
          const profile = profileResponse.data.data.profile;
          setProfileData(prev => ({
            ...prev,
            ...response.data.data.profile,
            profileCompleteness: profile.profileCompleteness || 0
          }));
        } else {
          // Fallback to response data if reload fails
          setProfileData(prev => ({
            ...prev,
            ...response.data.data.profile,
            profileCompleteness: response.data.data.profile.profileCompleteness || prev.profileCompleteness
          }));
        }
        setActiveEditSection(null);
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("Error saving profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Compress image for avatar upload
  const compressImage = (file, maxWidth = 200, maxHeight = 200, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
      };
    });
  };

  // Handle avatar upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 2MB before compression)
    if (file.size > 2 * 1024 * 1024) {
      alert("Image size must be less than 2MB");
      return;
    }

    try {
      setSaving(true);
      // Compress image before upload
      const compressedFile = await compressImage(file, 200, 200, 0.8);
      
      const response = await profileAPI.uploadAvatar(compressedFile);
      if (response.data.success) {
        // Reload profile to get updated data
        const profileResponse = await profileAPI.getProfile();
        if (profileResponse.data.success) {
          const profile = profileResponse.data.data.profile;
          setProfileData(prev => ({
            ...prev,
            profilePhoto: profile.profilePhoto || response.data.data.profilePhoto
          }));
          // Dispatch event to update Header avatar
          window.dispatchEvent(new Event('avatarUpdated'));
        } else {
          setProfileData(prev => ({
            ...prev,
            profilePhoto: response.data.data.profilePhoto
          }));
          // Dispatch event to update Header avatar
          window.dispatchEvent(new Event('avatarUpdated'));
        }
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      alert("Error uploading avatar. Please try again.");
    } finally {
      setSaving(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Handle resume upload
  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    try {
      setSaving(true);
      const response = await profileAPI.uploadResume(file);
      if (response.data.success) {
        // Reload profile to get updated completeness
        const profileResponse = await profileAPI.getProfile();
        if (profileResponse.data.success) {
          const profile = profileResponse.data.data.profile;
          setProfileData(prev => ({
            ...prev,
            resumeUrl: response.data.data.resumeUrl,
            resumeLastUpdated: response.data.data.resumeLastUpdated,
            profileCompleteness: profile.profileCompleteness || 0
          }));
        } else {
          // Fallback to response data if reload fails
          setProfileData(prev => ({
            ...prev,
            resumeUrl: response.data.data.resumeUrl,
            resumeLastUpdated: response.data.data.resumeLastUpdated,
            profileCompleteness: response.data.data.profile?.profileCompleteness || prev.profileCompleteness
          }));
        }
      }
    } catch (error) {
      console.error("Error uploading resume:", error);
      alert("Error uploading resume. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Initialize temp skills when entering edit mode
  const handleEditSkills = () => {
    if (activeEditSection === "skills") {
      // Cancel - reset temp skills
      setTempSkills([...profileData.skills]);
      setNewSkill("");
      setSkillLimitNotification("");
      setActiveEditSection(null);
    } else {
      // Enter edit mode - initialize temp skills
      setTempSkills([...profileData.skills]);
      setSkillLimitNotification("");
      setActiveEditSection("skills");
    }
  };

  // Save skills to backend
  const handleSaveSkills = async () => {
    try {
      setSaving(true);
      const response = await profileAPI.updateProfile({ skills: tempSkills });
      if (response.data.success) {
        // Reload profile to get updated completeness from database
        const profileResponse = await profileAPI.getProfile();
        if (profileResponse.data.success) {
          const profile = profileResponse.data.data.profile;
          setProfileData(prev => ({
            ...prev,
            skills: tempSkills,
            profileCompleteness: profile.profileCompleteness || 0
          }));
        } else {
          // Fallback to response data if reload fails
          setProfileData(prev => ({
            ...prev,
            skills: tempSkills,
            profileCompleteness: response.data.data.profile.profileCompleteness || prev.profileCompleteness
          }));
        }
        setActiveEditSection(null);
        setSkillLimitNotification("");
      }
    } catch (error) {
      console.error("Error saving skills:", error);
      alert("Error saving skills. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Add skill to temporary list (max 5)
  const handleAddSkill = () => {
    if (tempSkills.length >= 5) {
      setSkillLimitNotification("You can add up to 5 skills maximum.");
      setTimeout(() => setSkillLimitNotification(""), 3000);
      return;
    }
    if (newSkill.trim() && !tempSkills.includes(newSkill.trim())) {
      setTempSkills([...tempSkills, newSkill.trim()]);
      setNewSkill("");
      setSkillLimitNotification("");
    } else if (tempSkills.includes(newSkill.trim())) {
      setSkillLimitNotification("This skill is already added.");
      setTimeout(() => setSkillLimitNotification(""), 3000);
    }
  };

  // Remove skill from temporary list
  const handleRemoveSkill = (skill) => {
    setTempSkills(tempSkills.filter(s => s !== skill));
    setSkillLimitNotification("");
  };

  // Check which sections are completed
  const getCompletionStatus = () => {
    const sections = [
      {
        name: "Profile Photo",
        completed: !!(profileData.profilePhoto && profileData.profilePhoto.trim().length > 0),
        weight: 20
      },
      {
        name: "Contact Information",
        completed: profileData.fullName && profileData.phone && profileData.phone.trim().length > 0,
        weight: 20
      },
      {
        name: "Professional Summary",
        completed: profileData.professionalSummary && profileData.professionalSummary.trim().length > 0,
        weight: 15
      },
      {
        name: "Top Skills",
        completed: profileData.skills && profileData.skills.length > 0,
        weight: 20
      },
      {
        name: "Resume",
        completed: !!profileData.resumeUrl,
        weight: 25
      }
    ];
    return sections;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="profile-loading">
          <div className="app-spinner" />
          <p>Loading profile...</p>
        </div>
        <Footer />
        <Gotop />
      </>
    );
  }

  return (
    <>
      <Header />
      <section className="profile-page-section">
        <div className="tf-container">
          {/* 1. Profile Header */}
          <div className="profile-header-card">
            <div className="profile-header-gradient">
              <div className="profile-header-content-wrapper">
                <div className="profile-header-left">
                  <div className="profile-photo-container">
                    <div className="profile-photo-wrapper">
                      <img 
                        src={profileData.profilePhoto || defaultAvatar} 
                        alt="Profile" 
                        className="profile-photo" 
                      />
                      <label htmlFor="avatar-upload" className="avatar-upload-overlay">
                        <i className="icon-camera"></i>
                        <span>Change Photo</span>
                      </label>
                      <input
                        type="file"
                        id="avatar-upload"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        style={{ display: "none" }}
                        disabled={saving}
                      />
                    </div>
                  </div>
                  <div className="profile-header-info">
                    <h1 className="profile-name">{profileData.fullName || "Your Name"}</h1>
                    <p className="profile-headline">
                      {profileData.headline || "Add your professional headline"}
                    </p>
                    {profileData.profileCompleteness < 100 && (
                      <div className="completion-status-wrapper">
                        <div className="progress-bar-container">
                          <div className="progress-bar-background">
                            <div 
                              className="progress-bar-fill"
                              style={{ width: `${profileData.profileCompleteness}%` }}
                            ></div>
                          </div>
                          <span className="progress-percentage">{profileData.profileCompleteness}%</span>
                        </div>
                        <div className="completion-breakdown-inline">
                          {getCompletionStatus().map((section, index) => (
                            <span key={index} className={`completion-section-item ${section.completed ? 'completed' : 'incomplete'}`}>
                              {section.name} ({section.weight}%)
                              {index < getCompletionStatus().length - 1 && <span className="separator"> • </span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="profile-contact-info">
                  {profileData.email && (
                    <div className="contact-info-item">
                      <i className="icon-mail-envelope-closed"></i>
                      <span>{profileData.email}</span>
                    </div>
                  )}
                  {profileData.phone && (
                    <div className="contact-info-item">
                      <i className="icon-call"></i>
                      <span>{profileData.phone}</span>
                    </div>
                  )}
                  {(profileData.addressStreet || profileData.addressCity || profileData.addressState || profileData.country || profileData.addressZip) && (
                    <div className="contact-info-item">
                      <i className="icon-map-pin"></i>
                      <span>
                        {[
                          profileData.addressStreet,
                          profileData.addressCity,
                          profileData.addressState,
                          profileData.country,
                          profileData.addressZip
                        ].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 2. Professional Headline */}
          <div className="profile-section-card">
            <div className="section-header">
              <h2>Professional Headline</h2>
              <button 
                className="btn-edit"
                onClick={() => setActiveEditSection(activeEditSection === "headline" ? null : "headline")}
              >
                {activeEditSection === "headline" ? "Cancel" : "Edit"}
              </button>
            </div>
            <div className="section-content">
              {activeEditSection === "headline" ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>Professional Headline</label>
                    <input
                      type="text"
                      value={profileData.headline || ""}
                      onChange={(e) => setProfileData({ ...profileData, headline: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !saving) {
                          e.preventDefault();
                          saveSection({ headline: profileData.headline });
                        }
                      }}
                      placeholder="e.g., Senior Cloud Engineer"
                      maxLength={100}
                    />
                    <small className="form-hint">
                      Press Enter to save
                    </small>
                  </div>
                  <div className="form-actions">
                    <button
                      onClick={() => saveSection({ headline: profileData.headline })}
                      className="btn-save-section"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-display">
                  {profileData.headline ? (
                    <p className="summary-text">{profileData.headline}</p>
                  ) : (
                    <p className="empty-state">
                      <a href="#" onClick={(e) => { e.preventDefault(); setActiveEditSection("headline"); }}>Add headline</a>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 3. Professional Summary */}
          <div className="profile-section-card">
            <div className="section-header">
              <h2>Professional Summary</h2>
              <button 
                className="btn-edit"
                onClick={() => setActiveEditSection(activeEditSection === "summary" ? null : "summary")}
              >
                {activeEditSection === "summary" ? "Cancel" : "Edit"}
              </button>
            </div>
            <div className="section-content">
              {activeEditSection === "summary" ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>Professional Summary</label>
                    <textarea
                      value={profileData.professionalSummary}
                      onChange={(e) => setProfileData({ ...profileData, professionalSummary: e.target.value })}
                      placeholder="Brief summary about your background..."
                      rows={4}
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      onClick={() => saveSection({ professionalSummary: profileData.professionalSummary })}
                      className="btn-save-section"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-display">
                  {profileData.professionalSummary ? (
                    <p className="summary-text">{profileData.professionalSummary}</p>
                  ) : (
                    <p className="empty-state">
                      <a href="#" onClick={(e) => { e.preventDefault(); setActiveEditSection("summary"); }}>Add summary</a>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 4. Contact Information */}
          <div className="profile-section-card">
            <div className="section-header">
              <h2>Contact Information</h2>
              <button 
                className="btn-edit"
                onClick={() => setActiveEditSection(activeEditSection === "contact" ? null : "contact")}
              >
                {activeEditSection === "contact" ? "Cancel" : "Edit"}
              </button>
            </div>
            <div className="section-content">
              {activeEditSection === "contact" ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" value={profileData.email} disabled className="read-only" />
                    <small className="form-hint">Email cannot be changed for security reasons</small>
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="e.g., +1 234 567 8900"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Street Address (optional)</label>
                    <input
                      type="text"
                      value={profileData.addressStreet}
                      onChange={(e) => setProfileData(prev => ({ ...prev, addressStreet: e.target.value }))}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={profileData.addressCity}
                        onChange={(e) => setProfileData(prev => ({ ...prev, addressCity: e.target.value }))}
                        placeholder="City"
                      />
                    </div>
                    <div className="form-group">
                      <label>State / Region</label>
                      <input
                        type="text"
                        value={profileData.addressState}
                        onChange={(e) => setProfileData(prev => ({ ...prev, addressState: e.target.value }))}
                        placeholder="State / Region"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Country</label>
                      <div className="country-select-wrapper">
                        <input
                          type="text"
                          value={showCountryDropdown ? countrySearch : profileData.country}
                          onChange={(e) => {
                            setCountrySearch(e.target.value);
                            setShowCountryDropdown(true);
                          }}
                          onFocus={() => {
                            setCountrySearch("");
                            setShowCountryDropdown(true);
                          }}
                          placeholder="Type to search country..."
                          className="country-input"
                        />
                        {showCountryDropdown && (
                          <div className="country-dropdown">
                            {countries
                              .filter(country => 
                                country.toLowerCase().includes(countrySearch.toLowerCase())
                              )
                              .slice(0, 10)
                              .map((country) => (
                                <div
                                  key={country}
                                  className="country-option"
                                  onClick={() => {
                                    setProfileData(prev => ({ ...prev, country }));
                                    setCountrySearch("");
                                    setShowCountryDropdown(false);
                                  }}
                                >
                                  {country}
                                </div>
                              ))}
                            {countries.filter(country => 
                              country.toLowerCase().includes(countrySearch.toLowerCase())
                            ).length === 0 && countrySearch && (
                              <div className="country-option no-results">No countries found</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>ZIP / Postal Code</label>
                      <input
                        type="text"
                        value={profileData.addressZip}
                        onChange={(e) => setProfileData(prev => ({ ...prev, addressZip: e.target.value }))}
                        placeholder="ZIP / Postal Code"
                      />
                    </div>
                  </div>
                  <small className="form-hint">
                    Your contact details are shared only with Talsolution and hiring companies when relevant.
                  </small>
                  <button 
                    onClick={() => {
                      if (!profileData.phone || !profileData.phone.trim()) {
                        alert("Phone number is required.");
                        return;
                      }
                      saveSection({
                        phone: profileData.phone,
                        addressStreet: profileData.addressStreet,
                        addressCity: profileData.addressCity,
                        addressState: profileData.addressState,
                        country: profileData.country,
                        addressZip: profileData.addressZip
                      });
                    }}
                    className="btn-save-section"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              ) : (
                <div className="info-display">
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{profileData.email || "Not set"}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">{profileData.phone || "Not set"}</span>
                  </div>
                  {(profileData.addressStreet || profileData.addressCity || profileData.addressState || profileData.country || profileData.addressZip) && (
                    <div className="info-item">
                      <span className="info-label">Address:</span>
                      <span className="info-value">
                        {[
                          profileData.addressStreet,
                          profileData.addressCity,
                          profileData.addressState,
                          profileData.country,
                          profileData.addressZip
                        ].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 5. Top Skills & Resume - Two Column Layout */}
          <div className="profile-sections-grid">
            <div className="profile-section-card">
              <div className="section-header">
                <h2>Top Skills</h2>
                {activeEditSection === "skills" ? (
                  <button 
                    onClick={() => {
                      setActiveEditSection(null);
                      setTempSkills([...profileData.skills]);
                      setNewSkill("");
                      setSkillLimitNotification("");
                    }} 
                    className="btn-edit"
                  >
                    Cancel
                  </button>
                ) : (
                  <button 
                    className="btn-edit"
                    onClick={handleEditSkills}
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="section-content">
                {activeEditSection === "skills" ? (
                  <div className="edit-form">
                    <div className="form-group">
                      <label>Top Skills (up to 5)</label>
                      <div className="skills-input-container">
                        <input
                          type="text"
                          value={newSkill}
                          onChange={(e) => setNewSkill(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && handleAddSkill()}
                          placeholder="Type a skill and press Enter"
                          disabled={tempSkills.length >= 5}
                        />
                        <button 
                          type="button" 
                          onClick={handleAddSkill} 
                          className="btn-add"
                          disabled={tempSkills.length >= 5}
                        >
                          Add
                        </button>
                      </div>
                      {skillLimitNotification && (
                        <div className="skill-notification">
                          {skillLimitNotification}
                        </div>
                      )}
                      {tempSkills.length >= 5 && !skillLimitNotification && (
                        <small className="form-hint">Maximum 5 skills reached. Remove one to add another.</small>
                      )}
                      {tempSkills.length < 5 && !skillLimitNotification && (
                        <small className="form-hint">Choose your strongest skills. You can update them anytime.</small>
                      )}
                      <div className="skills-tags">
                        {tempSkills.map(skill => (
                          <span key={skill} className="skill-tag">
                            {skill}
                            <button 
                              type="button"
                              onClick={() => handleRemoveSkill(skill)}
                              className="tag-remove"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="form-actions">
                      <button 
                        onClick={handleSaveSkills}
                        className="btn-save-section"
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="info-display">
                    <div className="info-item">
                      <span className="info-label">Skills:</span>
                      <div className="tags-container">
                        {profileData.skills.length > 0 ? (
                          profileData.skills.map(skill => (
                            <span key={skill} className="tag">{skill}</span>
                          ))
                        ) : (
                          <span className="empty-state">No skills added</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="profile-section-card">
              <div className="section-header">
                <h2>Resume</h2>
              </div>
              <div className="section-content">
                <div className="resume-section">
                  {profileData.resumeUrl ? (
                    <div className="resume-info">
                      <div className="resume-details">
                        <i className="icon-file-doc"></i>
                        <div>
                          <a 
                            href={profileData.resumeUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="resume-link"
                          >
                            View Resume
                          </a>
                          {profileData.resumeLastUpdated && (
                            <p className="resume-date">
                              Last updated: {new Date(profileData.resumeLastUpdated).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="empty-state">No resume uploaded</p>
                  )}
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="resume-upload"
                      accept=".pdf,.doc,.docx"
                      onChange={handleResumeUpload}
                      style={{ display: "none" }}
                    />
                    <label htmlFor="resume-upload" className="btn-upload">
                      {profileData.resumeUrl ? "Replace Resume" : "Upload Resume"}
                    </label>
                    <small className="form-hint">
                      PDF, DOC, DOCX — Max 5MB
                    </small>
                    {!profileData.resumeUrl && (
                      <small className="form-hint" style={{ display: "block", marginTop: "8px" }}>
                        Uploading a resume improves your visibility.
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <Gotop />
    </>
  );
}

export default Profile;
